import React, { useMemo, useState } from 'react';
import type { ExecutionIncidentType } from '../routes/execution/incidentFacade';
import { Alert, Spin } from 'antd';
import type { Route } from '../../types';
import type { RouteViewModel } from '../../types/routeViewModel';
import {
  useApplyStopExecutionActionMutation,
  useCreateExecutionIncidentMutation,
  useGetExecutionEventsQuery,
  useGetExecutionIncidentsQuery,
  useGetExecutionProgressQuery,
  useResolveExecutionIncidentMutation,
  useGetOperationalCompletionQuery,
  useGetRouteOperationalProcessesQuery
} from '../../store/api/routesApi';
import {
  buildExecutionDashboardProjection,
  filterOperationalEvents,
  isAdvancedExecutionMode
} from '../routes/execution';
import { buildCargoHumanizationIndex, enrichExecutionCargo } from '../routes/execution/cargoHumanization';
import CargoExecutionGraph from './CargoExecutionGraph';
import ExecutionEventTimeline from './ExecutionEventTimeline';
import ExecutionCompactCard from './ExecutionCompactCard';
import ExecutionDispatcherWorkspace from './workspace/ExecutionDispatcherWorkspace';
import RouteCompletionModal from './RouteCompletionModal';
import { mergeStopAddresses, cargoAtStop } from './executionHelpers';
import { buildEffectiveExecutionView } from '../routes/execution/routeExecutionProjectionFacade';
import type { ExecutionActionSubmitPayload } from './ExecutionActionModal';
import { resolveActionSequence } from './ExecutionActionModal';
import { isBlockingIncidentType } from '../routes/execution/incidentFacade';
import {
  useGetCarrierAgreementByRouteQuery,
  useGetCarrierAgreementPenaltiesQuery
} from '../../store/api/carrierAgreementsApi';
import { useGetRouteExpensesQuery } from '../../store/api/financeApi';
import { countDraftExpensesForReadiness } from '../finance/domain/routeExpenseProjection';
import { isOperationalCoordinationMode } from '../operationalCoordination';
import { preferBackendCoordinationHydration } from '../operationalRecovery/operationalRecoveryStabilizationFlags';
import { mapCoordinationHydrationToFleet } from '../operationalCoordination/mapCoordinationHydration';
import { buildCoordinationFleetContext } from '../operationalCoordination/coordinationFleetContext';
import { useGetAllRoutesQuery } from '../../store/api/routesApi';

export type RouteExecutionTabProps = {
  route: Route;
  view: RouteViewModel;
  onSuccess?: () => void;
  compact?: boolean;
};

export const RouteExecutionTab: React.FC<RouteExecutionTabProps> = ({
  route,
  view,
  onSuccess,
  compact = false
}) => {
  const routeId = route.id;
  const [, setWorkflowModeTick] = useState(0);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [incidentReportRequest, setIncidentReportRequest] = useState<ExecutionIncidentType | null>(null);

  const {
    data: progressData,
    isLoading,
    isError,
    refetch
  } = useGetExecutionProgressQuery(routeId);

  const { data: events = [], isLoading: eventsLoading } = useGetExecutionEventsQuery(routeId);
  const { data: incidents = [], refetch: refetchIncidents } = useGetExecutionIncidentsQuery(routeId);
  const [applyAction, { isLoading: actionLoading }] = useApplyStopExecutionActionMutation();
  const [createIncident] = useCreateExecutionIncidentMutation();
  const [resolveIncident] = useResolveExecutionIncidentMutation();

  const { data: agreement } = useGetCarrierAgreementByRouteQuery(routeId);
  const { data: penaltyRows = [] } = useGetCarrierAgreementPenaltiesQuery(agreement?.id as number, {
    skip: !agreement?.id
  });
  const { data: expensesPayload, isLoading: expensesLoading } = useGetRouteExpensesQuery(
    { routeId, includeDraft: true, includeRejected: true },
    { skip: !routeId }
  );
  const expenses = useMemo(
    () => expensesPayload?.expenses ?? [],
    [expensesPayload?.expenses]
  );
  const { data: operationalCompletion } = useGetOperationalCompletionQuery(routeId);
  const { data: operationalProcesses, isLoading: processesLoading } =
    useGetRouteOperationalProcessesQuery(routeId, { skip: !routeId });

  const coordinationMode = isOperationalCoordinationMode();
  const useBackendCoordination = preferBackendCoordinationHydration();
  const needsClientFleet =
    coordinationMode &&
    !useBackendCoordination &&
    (route.driverId != null || route.vehicleId != null);

  const { data: fleetRoutesByDriver } = useGetAllRoutesQuery(
    { driverId: route.driverId!, limit: 60 },
    { skip: !needsClientFleet || route.driverId == null }
  );

  const { data: fleetRoutesBroad } = useGetAllRoutesQuery(
    { limit: 80 },
    { skip: !needsClientFleet || route.driverId != null }
  );

  const completionContext = useMemo(
    () => ({
      manualPenaltyCount: penaltyRows.filter((p) => p.requiresManualInput).length,
      recordedPenaltyCount: view.routePenalties?.length ?? 0,
      unconfirmedExpenseCount: countDraftExpensesForReadiness(expenses),
      hasLogistCommentOnRoute: false
    }),
    [penaltyRows, view.routePenalties, expenses]
  );

  const hydration = useMemo(() => {
    const raw = progressData ?? (route as Route & { executionHydration?: typeof progressData }).executionHydration;
    if (!raw) return null;
    const merged = mergeStopAddresses(raw, view.structure.stops);
    const effective = buildEffectiveExecutionView(merged);
    if (!effective?.usesProjection) return merged;
    return {
      ...merged,
      stops: effective.stops,
      cargo: effective.cargo,
      progress: effective.progress,
      financeReadiness: effective.financeReadiness
    };
  }, [progressData, route, view.structure.stops]);

  const fleetContext = useMemo(() => {
    if (!coordinationMode || !hydration) return undefined;
    if (hydration.coordinationContext) {
      return mapCoordinationHydrationToFleet(hydration);
    }
    if (!needsClientFleet) return undefined;
    const routes = fleetRoutesByDriver?.routes ?? fleetRoutesBroad?.routes ?? [];
    return buildCoordinationFleetContext(route, routes);
  }, [
    coordinationMode,
    hydration,
    needsClientFleet,
    route,
    fleetRoutesByDriver,
    fleetRoutesBroad
  ]);

  const forceAdvanced = isAdvancedExecutionMode();

  const dashboardProjection = useMemo(() => {
    if (!hydration) return null;
    return buildExecutionDashboardProjection({
      routeId,
      hydration,
      view,
      incidents,
      dashboard: view.executionDashboard ?? null,
      forceAdvanced,
      completionContext,
      completionSnapshot: operationalCompletion?.snapshot ?? null
    });
  }, [
    hydration,
    view,
    incidents,
    routeId,
    forceAdvanced,
    completionContext,
    operationalCompletion?.snapshot
  ]);

  const operationalEvents = useMemo(() => filterOperationalEvents(events), [events]);

  const cargoIndex = useMemo(
    () => buildCargoHumanizationIndex(view.cargo, view.applications),
    [view.cargo, view.applications]
  );

  const enrichedCargo = useMemo(
    () => (hydration ? enrichExecutionCargo(hydration.cargo, cargoIndex) : []),
    [hydration, cargoIndex]
  );

  const activeStopCargo = useMemo(() => {
    const op = dashboardProjection?.activeOperation;
    if (!hydration || !op) return [];
    return cargoAtStop(hydration.cargo, op.executionStopUid, op.operationType);
  }, [hydration, dashboardProjection?.activeOperation]);

  const handleActionSubmit = async (payload: ExecutionActionSubmitPayload) => {
    const { descriptor, occurredAt, comment, cargoQuantities, incident } = payload;

    if (descriptor.opensIncident && incident) {
      await createIncident({
        routeId,
        incidentType: incident.incidentType,
        comment: incident.comment || comment,
        relatedExecutionStopUid: descriptor.executionStopUid,
        blockingFlag: incident.blockingFlag,
        downtimeHours: incident.downtimeHours,
        occurredAt
      }).unwrap();
      await refetchIncidents();
      onSuccess?.();
      return;
    }

    const actions = resolveActionSequence(descriptor);
    if (actions.length === 0) return;

    const stopUid = descriptor.executionStopUid;
    if (!stopUid) return;

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const isLast = i === actions.length - 1;
      await applyAction({
        routeId,
        executionStopUid: stopUid,
        action,
        notes: comment || undefined,
        occurredAt,
        cargoQuantities: isLast ? cargoQuantities : undefined
      }).unwrap();
    }

    if (incident) {
      await createIncident({
        routeId,
        incidentType: incident.incidentType,
        comment: incident.comment || comment,
        relatedExecutionStopUid: stopUid,
        blockingFlag: incident.blockingFlag ?? isBlockingIncidentType(incident.incidentType),
        downtimeHours: incident.downtimeHours,
        occurredAt
      }).unwrap();
      await refetchIncidents();
    }

    await refetch();
    onSuccess?.();
  };

  const handleResolveIncident = async (incidentId: number, resolutionComment: string) => {
    setResolvingId(incidentId);
    try {
      await resolveIncident({ routeId, incidentId, resolutionComment }).unwrap();
      await refetchIncidents();
      onSuccess?.();
    } finally {
      setResolvingId(null);
    }
  };

  if (isLoading && !hydration) {
    return <Spin />;
  }

  if (isError && !hydration) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Данные исполнения недоступны"
        description="Для этого маршрута ещё не инициализировано исполнение. Сохраните аллокации или откройте карточку маршрута позже."
      />
    );
  }

  if (compact) {
    return (
      <ExecutionCompactCard
        routeId={routeId}
        hydration={hydration}
        dashboard={view.executionDashboard ?? null}
        legacyStatus={view.operation.status}
      />
    );
  }

  const p = dashboardProjection;

  return (
    <div className="entity-card-tab-body">
      {p ? (
        <>
          <ExecutionDispatcherWorkspace
            dashboard={p}
            routeId={routeId}
            hydration={hydration!}
            cargoAtStop={activeStopCargo}
            openDocumentTasks={view.execution.tasks.filter((t) => t.status !== 'COMPLETED').length}
            onSubmit={handleActionSubmit}
            onOpenCompletion={() => setCompletionOpen(true)}
            onModeChange={() => setWorkflowModeTick((n) => n + 1)}
            onResolveIncident={handleResolveIncident}
            onOpenIncidentReport={(preset) => setIncidentReportRequest(preset ?? 'downtime')}
            incidentReportRequest={incidentReportRequest}
            onClearIncidentRequest={() => setIncidentReportRequest(null)}
            resolvingIncidentId={resolvingId}
            actionLoading={actionLoading}
            routeExpenses={expenses}
            expensesLoading={expensesLoading}
            operationalProcesses={operationalProcesses}
            processesLoading={processesLoading}
            cargoIndex={cargoIndex}
            incidents={incidents}
            fleetContext={fleetContext}
            plannedLoadingDate={route.plannedLoadingDate}
            plannedUnloadingDate={route.plannedUnloadingDate}
            technicalDetails={
              hydration ? (
                <>
                  <CargoExecutionGraph
                    stops={hydration.stops}
                    cargo={enrichedCargo}
                    edges={hydration.edges}
                  />
                  <div style={{ marginTop: 12 }}>
                    <ExecutionEventTimeline events={operationalEvents} loading={eventsLoading} />
                  </div>
                </>
              ) : null
            }
          />

          <RouteCompletionModal
            open={completionOpen}
            route={route}
            onClose={() => setCompletionOpen(false)}
            onCompleted={() => {
              onSuccess?.();
            }}
          />
        </>
      ) : null}

      {view.execution.tasks.length > 0 ? (
        <Alert
          style={{ marginTop: 8 }}
          type="info"
          showIcon
          message={`Открытые задачи: ${view.execution.tasks.filter((t) => t.status !== 'COMPLETED').length}`}
        />
      ) : null}
    </div>
  );
};

export default RouteExecutionTab;
